// --- src/components/DonationPopup.js ---

import React, { useEffect } from 'react';
import { X, MoveRight } from 'lucide-react'; // Import MoveRight
import CopyableInfoBox from './CopyableInfoBox';

const DonationPopup = ({ onClose }) => {

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const cryptoDataList = [
    { "id": "eth", "label": "Ethereum", "network": "Ethereum", "type": "native", "address": "0xE8Cb08642F7B97E1d67120E615597b77A1B5eF64" },
    { "id": "usdc_eth", "label": "USDC (ERC20)", "network": "Ethereum", "type": "token", "address": "0xE8Cb08642F7B97E1d67120E615597b77A1B5eF64" },
    { "id": "btc", "label": "Bitcoin", "network": "Bitcoin", "type": "native", "address": "bc1qhvlxw3tphudfhexg4u7fjj85508n004j24skre" },
    { "id": "sol", "label": "Solana", "network": "Solana", "type": "native", "address": "AsEn2QbQmKQqrHy9k2R6ABZfNTK2ec8Kna18pxMqqr6c" },
    { "id": "usdt_trc20", "label": "USDT (TRC20)", "network": "Tron", "type": "token", "address": "TRsAXTUBykHEUYWEVgFEqnNCkzneCvyia2" }
  ];

  const paypalData = {
    "id": "paypal",
    "label": "Email",
    "address": "v.kryzhnii@gmail.com",
    "isEmail": true
  };

  const modalTitle = "Support Project R&D";
  // Final Intro Text (First sentence removed)
  const introText = <><strong className="font-semibold text-blue-600">Your donation helps</strong> fund the work needed to <strong className="font-semibold text-blue-600">build</strong> and <strong className="font-semibold text-blue-600">open-source the software that enables</strong> this fluid, cross-context interaction.</>;
  const paypalInstructions = <>To ensure your full donation reaches the project, please use the <strong className="font-medium text-blue-600">Sending to a friend</strong> option if available in your <strong className="font-medium text-blue-600">PayPal</strong> account.</>;
  const blogLinkPlaceholder = "https://blog.kryzhnii.me/blog/crosscontext-ux-design#61-key-future-directions";

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Increased width: max-w-4xl */}
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl relative text-gray-800 max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Area - Added flex for centering title */}
        <div className="relative px-6 py-3 border-b border-gray-200 flex items-center justify-center"> {/* Adjusted padding, added flex */}
          {/* Reduced title size */}
          <h2 className="text-lg lg:text-xl font-semibold text-center text-gray-900 flex-grow px-8"> {/* Added flex-grow and horizontal padding */}
            <span className="mx-auto max-w-sm">{modalTitle}</span>
          </h2>
          <button
            onClick={onClose}
            // Adjusted absolute positioning slightly if needed due to flex parent
            className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-400 hover:text-gray-700 bg-white/50 hover:bg-gray-100 rounded-full p-1 z-20"
            aria-label="Close donation popup"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="p-6 overflow-y-auto flex-1">
          <p className="text-center text-gray-600 mb-4 text-sm lg:text-base mx-auto max-w-md">
            {introText}
          </p>
          <p className="text-center text-sm mb-8">
            {/* Updated Icon */}
            <a href={blogLinkPlaceholder} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline inline-flex text-base font-bold items-center">
              Features roadmap <MoveRight size={16} className="ml-1" />
            </a>
          </p>

          {/* Desktop 3-Column Grid (or PayPal first on mobile) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 lg:gap-x-6 gap-y-6 flex flex-col lg:flex-row">

            {/* Column 3: PayPal Section (Appears first on mobile) */}
            <div className="lg:col-span-1 flex flex-col order-first lg:order-last">
              <div className="bg-blue-50/60 p-4 rounded-md border border-blue-300">
                <h3 className="text-lg font-medium mb-4 pb-2 text-gray-800 border-b border-blue-200">PayPal Donation</h3>
                <div className="flex-grow flex flex-col justify-between">
                  <div>
                    <p className="text-xs lg:text-sm text-gray-700 mb-3">{paypalInstructions}</p>
                    <p className="text-sm text-gray-800 mt-auto mb-3">Name: <strong className="font-semibold">Vladyslav Kryzhnii</strong></p>
                    <div className="space-y-3 mb-3">
                      <CopyableInfoBox
                        id={paypalData.id}
                        cryptoData={paypalData}
                      />
                    </div>
                  </div>
                  
                </div>
              </div>
            </div>

            {/* Column 1 & 2: Crypto Section */}
            <div className="lg:col-span-2 bg-gray-50 p-4 rounded-md border border-gray-300">
              <h3 className="text-lg font-medium mb-4 pb-2 text-gray-800 border-b border-gray-200">Crypto Donations</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {cryptoDataList.map((crypto) => (
                  <CopyableInfoBox
                    key={crypto.id}
                    id={crypto.id}
                    cryptoData={crypto}
                  />
                ))}
              </div>
            </div>

          </div> {/* End Grid */}



        </div> {/* End Scrollable Content Area */}

      </div> {/* End Modal Content Box */}
    </div> // End Modal Overlay
  );
};

export default DonationPopup;